// Master milestone storage implementation
import { 
  MasterMilestone, 
  MilestoneText, 
  MilestoneAns,
  MilestoneAnsText,
  ConcernAns,
  ConcernAnsText,
  MasterMilestoneData, 
  MilestoneWithText,
  MilestoneIndexes,
  MilestoneSyncMetadata,
  MilestoneQuery,
  MasterMilestoneStorage,
  MILESTONE_STORAGE_CONFIG,
  validateMasterMilestone,
  validateMilestoneText
} from '../types/milestones';
import { KeyValueStore } from './storage-strategy';

export class MasterMilestoneStorageImpl implements MasterMilestoneStorage {
  private keyValueStore: KeyValueStore;
  private cache: Map<string, MilestoneWithText> = new Map();
  private indexes: MilestoneIndexes | null = null;
  private lastIndexRebuild: number = 0;

  constructor(keyValueStore: KeyValueStore) {
    this.keyValueStore = keyValueStore;
  }

  // Data management methods
  async loadMasterData(): Promise<MasterMilestoneData | null> {
    try {
      const dataStr = await this.keyValueStore.getItem(MILESTONE_STORAGE_CONFIG.MASTER_DATA_KEY);
      if (!dataStr) {
        console.log('📋 No milestone data found in MASTER_DATA_KEY');
        
        // Debug: Check what keys are actually in storage
        console.log('🔍 STORAGE DEBUG: Master data check');
        console.log('  - Storage instance:', !!this.keyValueStore);
        console.log('  - Storage instance type:', typeof this.keyValueStore);
        console.log('  - Storage instance constructor:', this.keyValueStore.constructor.name);
        console.log('  - Storage instance hash:', this.keyValueStore.toString ? this.keyValueStore.toString().substring(0, 50) : 'no toString');
        console.log('  - MASTER_DATA_KEY:', MILESTONE_STORAGE_CONFIG.MASTER_DATA_KEY);
        
        // Try to list all keys if possible
        if ((this.keyValueStore as any).getAllKeys) {
          try {
            const allKeys = await (this.keyValueStore as any).getAllKeys();
            console.log('  - All storage keys:', allKeys);
            
            // Check for milestone-related keys
            const milestoneKeys = allKeys.filter((k: string) => k.includes('milestone') || k.includes('Milestone'));
            console.log('  - Milestone-related keys:', milestoneKeys);
            
            if (milestoneKeys.length > 0) {
              console.log('🔍 STORAGE TROUBLESHOOT: Found milestone-related keys, checking contents...');
              for (const key of milestoneKeys.slice(0, 3)) { // Check first 3 keys
                try {
                  const value = await this.keyValueStore.getItem(key);
                  console.log(`  - Key "${key}":`, value ? `${value.length} chars` : 'null/empty');
                } catch (error) {
                  console.log(`  - Key "${key}": Error reading - ${error}`);
                }
              }
            }
          } catch (error) {
            console.log('  - Could not list storage keys:', error);
          }
        }
        
        return null;
      }
      
      const data: MasterMilestoneData = JSON.parse(dataStr);
      console.log(`📄 Loaded milestone data: ${data.milestones.length} milestones, ${data.milestoneTexts.length} texts (version: ${data.dataVersion})`);
      
      // Validate data integrity on load
      await this.validateLoadedData(data);
      
      return data;
    } catch (error) {
      console.error('Error loading master milestone data:', error);
      return null;
    }
  }

  async saveMasterData(data: MasterMilestoneData): Promise<void> {
    try {
      // Validate data before saving
      await this.validateMasterData(data);
      
      // Save to MASTER_DATA_KEY as single source of truth
      const dataStr = JSON.stringify(data);
      
      await this.keyValueStore.setItem(
        MILESTONE_STORAGE_CONFIG.MASTER_DATA_KEY,
        dataStr
      );
      
      // Verify the save worked
      const verifyData = await this.keyValueStore.getItem(MILESTONE_STORAGE_CONFIG.MASTER_DATA_KEY);

      // Invalidate cache and indexes to force reload
      this.clearMemoryCache();
      
    } catch (error) {
      console.error('Error saving master milestone data:', error);
      throw error;
    }
  }

  async updateMilestones(milestones: MasterMilestone[]): Promise<void> {
    const existingData = await this.loadMasterData();
    if (!existingData) {
      throw new Error('No existing master data found');
    }

    // Merge milestones (replace existing by mvar)
    const milestoneMap = new Map(existingData.milestones.map(m => [m.mvar, m]));
    milestones.forEach(milestone => {
      milestoneMap.set(milestone.mvar, milestone);
    });

    const updatedData: MasterMilestoneData = {
      ...existingData,
      milestones: Array.from(milestoneMap.values()),
      lastSyncTimestamp: new Date().toISOString(),
    };

    await this.saveMasterData(updatedData);
  }

  async updateMilestoneTexts(texts: MilestoneText[]): Promise<void> {
    const existingData = await this.loadMasterData();
    if (!existingData) {
      throw new Error('No existing master data found');
    }

    // Merge texts (replace existing by mvar + language)
    const textMap = new Map(
      existingData.milestoneTexts.map(t => [`${t.mvar}_${t.languagetype}`, t])
    );
    texts.forEach(text => {
      textMap.set(`${text.mvar}_${text.languagetype}`, text);
    });

    const updatedData: MasterMilestoneData = {
      ...existingData,
      milestoneTexts: Array.from(textMap.values()),
      lastSyncTimestamp: new Date().toISOString(),
    };

    await this.saveMasterData(updatedData);
  }

  // Query methods with caching and indexing
  async getMilestonesByDomain(domain: string, language = MILESTONE_STORAGE_CONFIG.DEFAULT_LANGUAGE): Promise<MilestoneWithText[]> {
    const indexes = await this.getIndexes();
    const domainMilestones = indexes.sequenceByDomain.get(domain) || [];
    
    const results: MilestoneWithText[] = [];
    for (const milestone of domainMilestones) {
      const withText = await this.getMilestoneWithText(milestone, language);
      if (withText) {
        results.push(withText);
      }
    }
    
    return results;
  }

  async getMilestoneByMvar(mvar: string, language = MILESTONE_STORAGE_CONFIG.DEFAULT_LANGUAGE): Promise<MilestoneWithText | null> {
    // Check cache first
    const cacheKey = `${mvar}_${language}`;
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey)!;
    }

    const indexes = await this.getIndexes();
    const milestone = indexes.byMvar.get(mvar);
    
    if (!milestone) {
      return null;
    }

    const withText = await this.getMilestoneWithText(milestone, language);
    
    // Cache the result
    if (withText && this.cache.size < MILESTONE_STORAGE_CONFIG.MAX_CACHE_SIZE) {
      this.cache.set(cacheKey, withText);
    }
    
    return withText;
  }

  async getMilestonesSeqnum(domain: string, seqnum: number, language = MILESTONE_STORAGE_CONFIG.DEFAULT_LANGUAGE): Promise<MilestoneWithText[]> {
    const indexes = await this.getIndexes();
    const domainMilestones = indexes.byDomain.get(domain) || [];
    
    // Filter by sequence number
    const filtered = domainMilestones.filter(m => m.seqnum === seqnum);
    
    // Convert to MilestoneWithText
    const results: MilestoneWithText[] = [];
    for (const milestone of filtered) {
      const withText = await this.getMilestoneWithText(milestone, language);
      if (withText) {
        results.push(withText);
      }
    }
    
    return results;
  }

  async queryMilestones(query: MilestoneQuery): Promise<MilestoneWithText[]> {
    const indexes = await this.getIndexes();
    let candidates: MasterMilestone[] = [];

    if (query.domain) {
      candidates = indexes.byDomain.get(query.domain) || [];
    } else {
      candidates = Array.from(indexes.byMvar.values());
    }

    // Apply filters
    if (query.minAge !== undefined) {
      candidates = candidates.filter(m => m.itemage >= query.minAge!);
    }
    if (query.maxAge !== undefined) {
      candidates = candidates.filter(m => m.itemage <= query.maxAge!);
    }

    // Apply pagination
    const start = query.offset || 0;
    const end = query.limit ? start + query.limit : candidates.length;
    const paginated = candidates.slice(start, end);

    // Convert to MilestoneWithText
    const results: MilestoneWithText[] = [];
    for (const milestone of paginated) {
      const withText = await this.getMilestoneWithText(milestone, query.language);
      if (withText) {
        results.push(withText);
      }
    }

    return results;
  }

  // Answer management methods
  async updateMilestoneAnswers(answers: MilestoneAns[]): Promise<void> {
    const existingData = await this.loadMasterData();
    if (!existingData) {
      throw new Error('No existing master data found');
    }

    // Initialize arrays if they don't exist
    if (!existingData.milestoneAnswers) {
      existingData.milestoneAnswers = [];
    }

    // Merge answers (replace existing by ansid)
    const answerMap = new Map(existingData.milestoneAnswers.map(a => [a.ansid, a]));
    answers.forEach(answer => {
      answerMap.set(answer.ansid, answer);
    });

    const updatedData: MasterMilestoneData = {
      ...existingData,
      milestoneAnswers: Array.from(answerMap.values()),
      lastSyncTimestamp: new Date().toISOString(),
    };

    await this.saveMasterData(updatedData);
    this.clearCacheAndIndexes();
  }

  async updateConcernAnswers(answers: ConcernAns[]): Promise<void> {
    const existingData = await this.loadMasterData();
    if (!existingData) {
      throw new Error('No existing master data found');
    }

    // Initialize arrays if they don't exist
    if (!existingData.concernAnswers) {
      existingData.concernAnswers = [];
    }

    // Merge answers (replace existing by ansid)
    const answerMap = new Map(existingData.concernAnswers.map(a => [a.ansid, a]));
    answers.forEach(answer => {
      answerMap.set(answer.ansid, answer);
    });

    const updatedData: MasterMilestoneData = {
      ...existingData,
      concernAnswers: Array.from(answerMap.values()),
      lastSyncTimestamp: new Date().toISOString(),
    };

    await this.saveMasterData(updatedData);
    this.clearCacheAndIndexes();
  }

  async getMilestoneAnswers(mvar: string): Promise<MilestoneAns[]> {
    const indexes = await this.getIndexes();
    return indexes.answersByMvar.get(mvar) || [];
  }

  async getConcernAnswers(mvar: string): Promise<ConcernAns[]> {
    const indexes = await this.getIndexes();
    return indexes.concernAnswersByMvar.get(mvar) || [];
  }

  // Index management
  async buildIndexes(): Promise<MilestoneIndexes> {
    console.log('🔄 Building milestone indexes from MASTER_DATA_KEY...');
    const data = await this.loadMasterData();
    if (!data) {
      // Return empty indexes if no data exists
      console.log('⚠️ No master data available in MASTER_DATA_KEY - returning empty indexes');
      console.log('  📝 Use "Reload Standard Milestones" in Debug Tools to load AdminSegment data');
      const emptyIndexes: MilestoneIndexes = {
        byMvar: new Map(),
        byDomain: new Map(),
        textByMvar: new Map(),
        sequenceByDomain: new Map(),
        answersByMvar: new Map(),
        concernAnswersByMvar: new Map(),
      };
      this.indexes = emptyIndexes;
      this.lastIndexRebuild = Date.now();
      return emptyIndexes;
    }
    
    console.log(`📊 Building indexes for ${data.milestones.length} milestones and ${data.milestoneTexts.length} texts...`);
    console.log(`🏷️ Data source: MASTER_DATA_KEY, version: ${data.dataVersion}`);

    const indexes: MilestoneIndexes = {
      byMvar: new Map(),
      byDomain: new Map(),
      textByMvar: new Map(),
      sequenceByDomain: new Map(),
      answersByMvar: new Map(),
      concernAnswersByMvar: new Map(),
    };

    // Index milestones
    for (const milestone of data.milestones) {
      indexes.byMvar.set(milestone.mvar, milestone);
      
      // Group by domain (if domain field exists - for backwards compatibility)
      const domain = (milestone as any).domain;
      if (domain) {
        if (!indexes.byDomain.has(domain)) {
          indexes.byDomain.set(domain, []);
        }
        indexes.byDomain.get(domain)!.push(milestone);
      }
    }

    // Sort domain groups by sequence number
    for (const [domain, milestones] of indexes.byDomain) {
      const sorted = milestones.sort((a, b) => a.seqnum - b.seqnum);
      indexes.sequenceByDomain.set(domain, sorted);
    }

    // Index texts
    for (const text of data.milestoneTexts) {
      if (!indexes.textByMvar.has(text.mvar)) {
        indexes.textByMvar.set(text.mvar, new Map());
      }
      indexes.textByMvar.get(text.mvar)!.set(text.languagetype, text);
    }

    // Index answers if available
    if (data.milestoneAnswers) {
      for (const answer of data.milestoneAnswers) {
        if (!indexes.answersByMvar.has(answer.mvar)) {
          indexes.answersByMvar.set(answer.mvar, []);
        }
        indexes.answersByMvar.get(answer.mvar)!.push(answer);
      }
    }

    if (data.concernAnswers) {
      for (const answer of data.concernAnswers) {
        if (!indexes.concernAnswersByMvar.has(answer.mvar)) {
          indexes.concernAnswersByMvar.set(answer.mvar, []);
        }
        indexes.concernAnswersByMvar.get(answer.mvar)!.push(answer);
      }
    }

    this.indexes = indexes;
    this.lastIndexRebuild = Date.now();

    console.log(`✅ Index building complete:`);
    console.log(`  - Domains indexed: ${indexes.byDomain.size}`);
    console.log(`  - Milestones by domain:`, Array.from(indexes.byDomain.entries()).map(([domain, milestones]) => `${domain}: ${milestones.length}`));
    console.log(`  - Total milestone texts: ${data.milestoneTexts.length}`);
    
    return indexes;
  }

  async validateDataIntegrity(): Promise<MilestoneSyncMetadata> {
    const data = await this.loadMasterData();
    if (!data) {
      throw new Error('No master data available for validation');
    }

    const indexes = await this.getIndexes();
    const metadata: MilestoneSyncMetadata = {
      totalMilestones: data.milestones.length,
      totalTexts: data.milestoneTexts.length,
      totalAnswers: data.milestoneAnswers?.length || 0,
      totalConcernAnswers: data.concernAnswers?.length || 0,
      supportedLanguages: Array.from(new Set(data.milestoneTexts.map(t => t.languagetype))),
      lastFullSync: data.lastSyncTimestamp,
      incrementalSyncAvailable: true,
      dataIntegrity: {
        milestonesWithoutText: [],
        orphanedTexts: [],
        duplicateSequences: [],
        orphanedAnswers: [],
      },
    };

    // Check data integrity - milestones without text
    for (const milestone of data.milestones) {
      const hasText = indexes.textByMvar.has(milestone.mvar);
      if (!hasText) {
        metadata.dataIntegrity.milestonesWithoutText.push(milestone.mvar);
      }
    }

    // Check orphaned texts
    for (const text of data.milestoneTexts) {
      const hasMilestone = indexes.byMvar.has(text.mvar);
      if (!hasMilestone) {
        metadata.dataIntegrity.orphanedTexts.push(`${text.mvar}_${text.languagetype}`);
      }
    }

    // Check for duplicate sequence numbers
    for (const [domain, milestones] of indexes.byDomain) {
      const seqNums = milestones.map(m => m.seqnum);
      const duplicates = seqNums.filter((num, index) => seqNums.indexOf(num) !== index);
      if (duplicates.length > 0) {
        metadata.dataIntegrity.duplicateSequences.push({
          domain,
          duplicatedSeqNums: Array.from(new Set(duplicates)),
        });
      }
    }

    return metadata;
  }

  // Utility methods
  async getDataVersion(): Promise<string> {
    const data = await this.loadMasterData();
    return data?.dataVersion || '0.0.0';
  }

  async getSyncMetadata(): Promise<MilestoneSyncMetadata> {
    return this.validateDataIntegrity();
  }

  private clearCacheAndIndexes(): void {
    this.cache.clear();
    this.indexes = null;
    this.lastIndexRebuild = 0;
  }

  async clearCache(): Promise<void> {
    this.clearMemoryCache();
    // Could also clear structured storage cache if needed
  }

  async preloadForDomain(domain: string): Promise<void> {
    const milestones = await this.getMilestonesByDomain(domain);
    console.log(`Preloaded ${milestones.length} milestones for domain: ${domain}`);
  }

  // Private helper methods
  private async getIndexes(): Promise<MilestoneIndexes> {
    const now = Date.now();
    const shouldRebuild = !this.indexes || 
      (now - this.lastIndexRebuild) > MILESTONE_STORAGE_CONFIG.INDEX_REBUILD_INTERVAL;

    if (shouldRebuild) {
      return this.buildIndexes();
    }

    return this.indexes!;
  }

  private async getMilestoneWithText(
    milestone: MasterMilestone, 
    language = MILESTONE_STORAGE_CONFIG.DEFAULT_LANGUAGE
  ): Promise<MilestoneWithText | null> {
    const indexes = await this.getIndexes();
    const textMap = indexes.textByMvar.get(milestone.mvar);
    
    let text = '';
    let isAvailable = false;

    if (textMap) {
      // Try requested language first
      let milestoneText = textMap.get(language);
      
      // Fallback to default language if requested not available
      if (!milestoneText && language !== MILESTONE_STORAGE_CONFIG.DEFAULT_LANGUAGE) {
        milestoneText = textMap.get(MILESTONE_STORAGE_CONFIG.DEFAULT_LANGUAGE);
      }
      
      if (milestoneText) {
        text = milestoneText.htmltext;
        isAvailable = true;
      }
    }

    return {
      ...milestone,
      text,
      isAvailable,
    };
  }

  private clearMemoryCache(): void {
    this.cache.clear();
    this.indexes = null;
    this.lastIndexRebuild = 0;
  }



  // Development utility for loading mock data
  async loadMockData(): Promise<void> {
    console.log('🚀 Generating mock milestone data...');
    const mockData = generateMockMilestoneData();
    console.log(`📦 Generated ${mockData.milestones.length} milestones and ${mockData.milestoneTexts.length} texts`);
    
    await this.saveMasterData(mockData);
    console.log('💾 Mock milestone data saved to storage');
    
    // Verify the data was saved correctly
    const savedData = await this.loadMasterData();
    if (savedData) {
      console.log(`✅ Verification: ${savedData.milestones.length} milestones and ${savedData.milestoneTexts.length} texts available in storage`);
    } else {
      console.error('❌ Verification failed: No data found in storage after saving');
      throw new Error('Mock data was not saved correctly');
    }
    
    // Force rebuild indexes with the new data
    await this.buildIndexes();
    console.log('🔄 Indexes rebuilt successfully');
  }

  private async validateLoadedData(data: MasterMilestoneData): Promise<void> {
    let errors: string[] = [];

    // Validate milestones
    for (const milestone of data.milestones) {
      const milestoneErrors = validateMasterMilestone(milestone);
      errors = errors.concat(milestoneErrors.map(e => `Milestone ${milestone.mvar}: ${e}`));
    }

    // Validate texts
    for (const text of data.milestoneTexts) {
      const textErrors = validateMilestoneText(text);
      errors = errors.concat(textErrors.map(e => `Text ${text.mvar}_${text.languagetype}: ${e}`));
    }

    if (errors.length > 0) {
      console.warn('Data validation warnings:', errors);
      // Don't throw - just warn, as data might be recoverable
    }
  }

  private async validateMasterData(data: MasterMilestoneData): Promise<void> {
    if (!data.milestones || !Array.isArray(data.milestones)) {
      throw new Error('Invalid milestones array');
    }
    
    if (!data.milestoneTexts || !Array.isArray(data.milestoneTexts)) {
      throw new Error('Invalid milestoneTexts array');
    }

    // Check for duplicate mvars in milestones
    const mvarids = new Set();
    for (const milestone of data.milestones) {
      // Skip milestones with undefined/null/empty mvar values
      if (!milestone.mvar || milestone.mvar.trim() === '') {
        console.warn('⚠️ Skipping milestone with invalid mvar:', milestone);
        continue;
      }
      
      if (mvarids.has(milestone.mvar)) {
        throw new Error(`Duplicate milestone mvar: ${milestone.mvar}`);
      }
      mvarids.add(milestone.mvar);
    }

    // Check for duplicate text entries
    const textKeys = new Set();
    for (const text of data.milestoneTexts) {
      const key = `${text.mvar}_${text.languagetype}`;
      if (textKeys.has(key)) {
        throw new Error(`Duplicate milestone text: ${key}`);
      }
      textKeys.add(key);
    }
  }
}

// Mock data generation for development
export const generateMockMilestoneData = (): MasterMilestoneData => {
  console.log('🎯 Starting mock milestone data generation...');
  
  const milestones: MasterMilestone[] = [];
  const milestoneTexts: MilestoneText[] = [];
  const milestoneAnswers: MilestoneAns[] = [];
  const milestoneAnswerTexts: MilestoneAnsText[] = [];

  // Import domain functions - we'll need to get these from profile types
  const domains = [
    { name: 'Communication', key: 'communication' },
    { name: 'Motor', key: 'motor' },
    { name: 'Cognitive', key: 'cognitive' },
    { name: 'Social-Emotional', key: 'socialEmotional' },
    { name: 'Fine Motor', key: 'fineMotor' },
    { name: 'Gross Motor', key: 'grossMotor' }
  ];

  console.log(`📋 Processing ${domains.length} domains...`);

  const answerValues = ['yes', 'sometimes', 'not-yet'] as const;
  const answerTexts = ['Yes', 'Sometimes', 'Not Yet'];

  // Generate milestones for each domain
  domains.forEach((domain, domainIndex) => {
    console.log(`📝 Generating milestones for domain ${domainIndex + 1}/${domains.length}: ${domain.name} (${domain.key})`);
    
    for (let i = 1; i <= 60; i++) {
      const mvar = `${domain.key}_${i}`;
      
      // Create milestone
      milestones.push({
        mvar,
        domain: domain.key,
        itemage: i,
        seqnum: i,
        lastUpdated: new Date().toISOString(),
        version: 1
      });

      // Create milestone text
      milestoneTexts.push({
        mvar,
        languagetype: 'en',
        htmltext: `${domain.name} milestone #${i}`,
        lastUpdated: new Date().toISOString(),
        version: 1
      });

      // Create milestone answers
      answerValues.forEach((value, index) => {
        const ansid = `${mvar}_${value}`;
        
        milestoneAnswers.push({
          ansid,
          mvar,
          value,
          seqnum: index + 1,
          lastUpdated: new Date().toISOString(),
          version: 1
        });

        // Create milestone answer text
        milestoneAnswerTexts.push({
          ansid,
          languagetype: 'en',
          htmltext: answerTexts[index],
          lastUpdated: new Date().toISOString(),
          version: 1
        });
      });
    }
    
    console.log(`✅ Domain ${domain.name}: Generated 60 milestones with ${60 * 3} answer options`);
  });

  console.log(`Generated mock data: ${milestones.length} milestones, ${milestoneTexts.length} texts across ${domains.length} domains`);
  console.log('Domains:', domains.map(d => `${d.key} (${d.name})`));
  console.log('Sample milestones:', milestones.slice(0, 5).map(m => ({ mvar: m.mvar, domain: m.domain, seqnum: m.seqnum, itemage: m.itemage })));
  console.log('Sample texts:', milestoneTexts.slice(0, 5).map(t => ({ mvar: t.mvar, htmltext: t.htmltext })));

  return {
    milestones,
    milestoneTexts,
    milestoneAnswers,
    milestoneAnswerTexts,
    concernAnswers: [],
    concernAnswerTexts: [],
    languages: [
      {
        code: 'en',
        name: 'English',
        isDefault: true,
        isSupported: true
      }
    ],
    lastSyncTimestamp: new Date().toISOString(),
    dataVersion: '1.0.0'
  };
};

// Factory function
export const createMasterMilestoneStorage = (keyValueStore: KeyValueStore): MasterMilestoneStorage => {
  return new MasterMilestoneStorageImpl(keyValueStore);
};